import {getAvailableSteps} from "../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";

/**
 * getAvailableSteps() walks the whole wizard graph (up to 50 steps, each step scanning every
 * wizard group), so it must not be called per cart item. This creates a short lived cache that
 * is shared by everything rendered in one shopping cart pass: the same (wizardItem,
 * wizardSelectedValues) pair is only ever computed once.
 *
 * The cache keys on object identity, so it is safe to keep for a single render pass only -
 * do not store it on the module level.
 *
 * @returns {function(wizardItemInterface, object): array}
 */
const NO_SELECTED_VALUES = {};

export const createWizardStepsCache = () => {
  const cache = new Map(); // wizardItem -> Map(wizardSelectedValues -> steps)

  return (wizardItem, wizardSelectedValues) => {
    if (!wizardItem) {
      return []
    }
    const valuesKey = wizardSelectedValues || NO_SELECTED_VALUES;
    let stepsByValues = cache.get(wizardItem);
    if (!stepsByValues) {
      stepsByValues = new Map();
      cache.set(wizardItem, stepsByValues);
    }
    if (stepsByValues.has(valuesKey)) {
      return stepsByValues.get(valuesKey)
    }
    const steps = getAvailableSteps(wizardItem, wizardSelectedValues, true);
    stepsByValues.set(valuesKey, steps);
    return steps
  }
};

/**
 * collects every wizard group id reachable from the given steps, including merged groups
 * @param wizardSteps {array}
 * @returns {Set<string>}
 */
const getReachableWizardGroupIds = (wizardSteps) => {
  const groupIds = new Set();
  wizardSteps.forEach(step => {
    step.stepGroups.forEach(wizGroup => {
      groupIds.add(wizGroup.id);
      if (wizGroup.mergedGroups) {
        wizGroup.mergedGroups.forEach(mergedGroup => groupIds.add(mergedGroup.id));
      }
    })
  });
  return groupIds
};

/**
 * A cart item that came from a wizard can only be edited while its wizard group is still
 * reachable with the current selections. This used to be resolved inside every single
 * edit button (one full getAvailableSteps() walk per cart item); here it is resolved once for
 * the whole cart, grouped by (quoteIndex, wizardItemId).
 *
 * Mirrors the previous per item logic exactly:
 *  - no fromWizardGroupId, or no allWizardsValues at all -> editable
 *  - no matching wizard item / no selected values -> not editable
 *  - otherwise editable when the group is still reachable
 *
 * @param cartItems {array}
 * @param wizardList {array}
 * @param allWizardsValues {array}
 * @param getWizardSteps {function} cache from createWizardStepsCache()
 * @returns {Map<*, boolean>} cartItem.id -> isEditable
 */
export const buildWizardEditableMap = (cartItems, wizardList, allWizardsValues, getWizardSteps) => {
  const editableMap = new Map();
  if (!cartItems || !cartItems.length) {
    return editableMap
  }
  if (!allWizardsValues) {
    cartItems.forEach(cartItem => editableMap.set(cartItem.id, true));
    return editableMap
  }

  const groupIdsByWizardKey = new Map(); // `${currentQuote}|${fromWizardId}` -> Set<groupId>|null

  cartItems.forEach(cartItem => {
    if (!cartItem.fromWizardGroupId) {
      editableMap.set(cartItem.id, true);
      return
    }

    const wizardKey = cartItem.currentQuote + "|" + cartItem.fromWizardId;
    if (!groupIdsByWizardKey.has(wizardKey)) {
      const neededObject = allWizardsValues.find(wizValueObject => (wizValueObject.quoteIndex === cartItem.currentQuote)
        && (wizValueObject.wizardItemId === cartItem.fromWizardId));
      const selectedWizValues = neededObject && neededObject.wizardSelectedValues ? neededObject.wizardSelectedValues : null;
      const wizardItem = wizardList ? wizardList.find(wizItem => wizItem.id === cartItem.fromWizardId) : null;
      // null means "no steps could be resolved" -> nothing is reachable, same as the old !neededStep
      groupIdsByWizardKey.set(wizardKey, wizardItem && selectedWizValues
        ? getReachableWizardGroupIds(getWizardSteps(wizardItem, selectedWizValues))
        : null);
    }

    const reachableGroupIds = groupIdsByWizardKey.get(wizardKey);
    editableMap.set(cartItem.id, !!reachableGroupIds && reachableGroupIds.has(cartItem.fromWizardGroupId));
  });

  return editableMap
};
